import styled from '@emotion/styled';

export const Page = styled.main`
	padding-top: calc(${(props) => props.theme.heights.header} + 6rem);
	padding-bottom: 10rem;
	background: ${(props) => props.theme.ui.surface.subtle};

	@media screen and (max-width: 1180px) {
		padding-top: calc(${(props) => props.theme.heights.headerMobile} + 5rem);
	}
`;

export const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${(props) => props.theme.scale.space2xl};
	max-width: ${(props) => props.theme.scale.contentMaxWidth};
	width: 100%;
	margin: 0 auto;
	padding: 0 ${(props) => props.theme.scale.contentPaddingX};
	box-sizing: border-box;
`;

export const PageHeader = styled.header`
	display: flex;
	flex-direction: column;
	gap: ${(props) => props.theme.scale.space2xs};
`;

export const Eyebrow = styled.span`
	font-size: ${(props) => props.theme.fontSize.sizeXs};
	font-weight: ${(props) => props.theme.fontWeight.weightBold};
	line-height: 1;
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.label};
	text-transform: uppercase;
	color: ${(props) => props.theme.ui.interactive.primary};
`;

export const Title = styled.h1`
	margin: 0;
	font-size: ${(props) => props.theme.fontSize.size4xl};
	font-weight: ${(props) => props.theme.fontWeight.weightBold};
	line-height: 1.25;
	letter-spacing: ${(props) => props.theme.ui.letterSpacing.tight};
	color: ${(props) => props.theme.ui.text.primary};
`;

export const Subtitle = styled.p`
	margin: 0;
	max-width: 52ch;
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	line-height: 1.55;
	color: ${(props) => props.theme.ui.text.secondary};
`;

export const Stepper = styled.ol`
	display: flex;
	align-items: flex-start;
	gap: 0;
	width: 100%;
	margin: 0;
	padding: 0;
	list-style: none;
`;

export const StepItem = styled.li<{ $clickable?: boolean; $state: 'upcoming' | 'current' | 'complete' }>`
	display: flex;
	flex: 1;
	flex-direction: column;
	align-items: center;
	gap: ${(props) => props.theme.scale.spaceSm};
	min-width: 0;
	position: relative;
	cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

	&:not(:last-child)::after {
		content: '';
		position: absolute;
		top: 1.6rem;
		left: calc(50% + 1.8rem);
		width: calc(100% - 3.6rem);
		height: 2px;
		background: ${({ $state, theme }) =>
			$state === 'complete' ? theme.ui.interactive.primary : theme.ui.border.default};
		transition: background-color ${({ theme }) => theme.effects.transitionBase};
	}
`;

export const StepCircle = styled.span<{ $state: 'upcoming' | 'current' | 'complete' }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 3.2rem;
	height: 3.2rem;
	border-radius: ${(props) => props.theme.effects.radiusFull};
	border: 2px solid
		${({ $state, theme }) =>
			$state === 'current' || $state === 'complete' ? theme.ui.interactive.primary : theme.ui.border.strong};
	background: ${({ $state, theme }) =>
		$state === 'current' || $state === 'complete' ? theme.ui.interactive.primary : theme.ui.surface.page};
	color: ${({ $state, theme }) => ($state === 'upcoming' ? theme.ui.text.muted : theme.ui.text.inverse)};
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${(props) => props.theme.fontWeight.weightBold};
	line-height: 1;
	transition:
		border-color ${(props) => props.theme.effects.transitionFast},
		background-color ${(props) => props.theme.effects.transitionFast},
		color ${(props) => props.theme.effects.transitionFast};
	box-shadow: ${({ $state, theme }) => ($state === 'current' ? theme.effects.shadowSm : 'none')};
`;

export const StepLabel = styled.span<{ $state: 'upcoming' | 'current' | 'complete' }>`
	text-align: center;
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${({ $state, theme }) =>
		$state === 'current' ? theme.fontWeight.weightBold : theme.fontWeight.weightSemi};
	line-height: 1.35;
	color: ${({ $state, theme }) =>
		$state === 'current' ? theme.ui.interactive.primary : $state === 'complete' ? theme.ui.text.primary : theme.ui.text.muted};
	word-break: keep-all;
`;

export const StepperMobile = styled.ol`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: ${(props) => props.theme.scale.spaceMd};
	margin: 0;
	padding: 0;
	list-style: none;
`;

export const StepDot = styled.li<{ $state: 'upcoming' | 'current' | 'complete' }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.6rem;

	&::before {
		content: '';
		width: 1rem;
		height: 1rem;
		border-radius: ${(props) => props.theme.effects.radiusFull};
		background: ${({ $state, theme }) =>
			$state === 'current' || $state === 'complete' ? theme.ui.interactive.primary : theme.ui.border.strong};
		box-shadow: ${({ $state, theme }) => ($state === 'current' ? `0 0 0 4px ${theme.ui.interactive.primaryMuted}` : 'none')};
	}

	span {
		font-size: ${(props) => props.theme.fontSize.sizeXs};
		font-weight: ${({ $state, theme }) =>
			$state === 'current' ? theme.fontWeight.weightBold : theme.fontWeight.weightRegular};
		color: ${({ $state, theme }) => ($state === 'current' ? theme.ui.interactive.primary : theme.ui.text.muted)};
	}
`;
